/* In-browser mock of backend/server.py — serves the full ERP API from static data
   so the frontend never makes network calls. */

import { db, reseed } from "./staticData";

const nowIso = () => new Date().toISOString();
const DAY = 864e5;
const nid = () =>
  typeof crypto !== "undefined" && crypto.randomUUID ? crypto.randomUUID() : `id-${Date.now()}-${Math.random().toString(16).slice(2)}`;
const r2 = (x) => Math.round(x * 100) / 100;

class ApiError extends Error {
  constructor(status, detail) {
    super(detail);
    this.response = { status, data: { detail } };
  }
}
const err = (s, d) => { throw new ApiError(s, d); };
const notFound = (name) => err(404, `${name} not found`);

function audit(collection, recordId, action, actor, changes) {
  db.audit_logs.push({
    id: nid(), collection, record_id: recordId, action, actor, changes,
    timestamp: nowIso(),
  });
}

// ---------------- Generic CRUD ----------------
const CRUD_ENTITIES = {
  entities: "entities", counterparties: "counterparties", banks: "banks",
  brokers: "brokers", vessels: "vessels", commodities: "commodities",
  "fx-rates": "fx_rates", "futures-prices": "futures_prices",
  "purchase-contracts": "purchase_contracts", "sale-contracts": "sale_contracts",
  "contract-links": "contract_links", "freight-fixtures": "freight_fixtures",
  "port-calls": "port_calls", "documents": "documents", "hedge-positions": "hedge_positions",
  "letters-of-credit": "letters_of_credit", "accounts": "accounts",
  "journal-entries": "journal_entries", "posting-rules": "posting_rules", "tasks": "tasks",
  "periods": "periods", "invoices": "invoices",
};

function listItems(name) {
  const coll = db[CRUD_ENTITIES[name]];
  return [...coll].sort((a, b) => (b.created_at || "").localeCompare(a.created_at || ""));
}

function getItem(name, id) {
  const doc = db[CRUD_ENTITIES[name]].find((d) => d.id === id);
  if (!doc) notFound(name);
  return doc;
}

function createItem(name, payload) {
  const actor = payload._actor || "system";
  const doc = { ...payload };
  delete doc._actor;
  doc.id = doc.id || nid();
  doc.created_at = nowIso();
  doc.updated_at = nowIso();
  db[CRUD_ENTITIES[name]].push(doc);
  audit(CRUD_ENTITIES[name], doc.id, "create", actor);
  return doc;
}

function updateItem(name, id, payload) {
  const actor = payload._actor || "system";
  const coll = db[CRUD_ENTITIES[name]];
  const doc = coll.find((d) => d.id === id);
  if (!doc) notFound(name);
  const changes = { ...payload };
  delete changes._actor;
  delete changes.id;
  changes.updated_at = nowIso();
  Object.assign(doc, changes);
  audit(CRUD_ENTITIES[name], id, "update", actor, changes);
  return doc;
}

function deleteItem(name, id) {
  const coll = db[CRUD_ENTITIES[name]];
  const i = coll.findIndex((d) => d.id === id);
  if (i >= 0) coll.splice(i, 1);
  audit(CRUD_ENTITIES[name], id, "delete", "system");
  return { ok: true };
}

// ---------------- Journal / accounting helpers ----------------
const periodKey = (iso) => (iso || nowIso()).slice(0, 7);

function assertPeriodOpen(entryDate) {
  const key = periodKey(entryDate);
  const p = db.periods.find((x) => x.key === key);
  if (p && p.locked) err(423, `Accounting period ${key} is locked. Posting is not allowed.`);
}

function postJournal(entityId, event, description, lines, sourceType = null, sourceId = null,
  vesselId = null, currency = "USD", fxRate = 1.0, entryDate = null, status = "Posted",
  entryType = "Auto", maker = null) {
  if (status === "Posted") assertPeriodOpen(entryDate);
  const je = {
    id: nid(), entity_id: entityId, event, description, lines,
    source_type: sourceType, source_id: sourceId, vessel_id: vesselId,
    currency, fx_rate: fxRate, entry_date: entryDate || nowIso(),
    status, entry_type: entryType, maker, checker: null,
    created_at: nowIso(), updated_at: nowIso(),
  };
  db.journal_entries.push(je);
  return je;
}

// ---------------- Business endpoints ----------------
function createManualJe(payload) {
  const lines = payload.lines || [];
  if (lines.length < 2) err(400, "At least two lines required");
  const dr = r2(lines.reduce((s, l) => s + Number(l.debit || 0), 0));
  const cr = r2(lines.reduce((s, l) => s + Number(l.credit || 0), 0));
  if (dr !== cr) err(400, `Unbalanced entry: debit ${dr} != credit ${cr}`);
  if (dr === 0) err(400, "Entry amount cannot be zero");
  assertPeriodOpen(payload.entry_date);
  const je = postJournal(
    payload.entity_id, payload.event || "Manual Adjustment",
    payload.description || "Manual journal entry", lines, "manual", null,
    payload.vessel_id, payload.currency || "USD", Number(payload.fx_rate || 1.0),
    payload.entry_date, "Pending Approval", "Manual", payload.maker || "system",
  );
  audit("journal_entries", je.id, "manual_create", payload.maker || "system");
  return je;
}

function approveJe(jid, payload) {
  const je = getItem("journal-entries", jid);
  if (je.status !== "Pending Approval") err(400, "Only pending entries can be approved");
  assertPeriodOpen(je.entry_date);
  const checker = (payload || {}).checker || "system";
  je.status = "Posted"; je.checker = checker; je.updated_at = nowIso();
  audit("journal_entries", jid, "approve", checker);
  return { ok: true, status: "Posted" };
}

function rejectJe(jid, payload) {
  const je = getItem("journal-entries", jid);
  const checker = (payload || {}).checker || "system";
  je.status = "Rejected"; je.checker = checker; je.updated_at = nowIso();
  audit("journal_entries", jid, "reject", checker);
  return { ok: true, status: "Rejected" };
}

function togglePeriod(pid, lock) {
  const p = getItem("periods", pid);
  p.locked = lock; p.status = lock ? "Locked" : "Open"; p.updated_at = nowIso();
  audit("periods", pid, lock ? "lock" : "unlock", "system");
  return { ok: true };
}

const nextSeq = (prefix) => `${prefix}-2026-${String(db.invoices.length + 1).padStart(3, "0")}`;

function raiseProvisionalInvoice(payload) {
  const sc = db.sale_contracts.find((x) => x.id === payload.sale_contract_id);
  const amount = Number(payload.amount) || (sc ? sc.quantity * sc.price : 0);
  const entityId = payload.entity_id || (sc ? sc.entity_id : null);
  const vesselId = payload.vessel_id || (sc ? (sc.vessel_ids || [null])[0] : null);
  const currency = payload.currency || (sc ? sc.currency : "USD");
  const invNo = payload.invoice_number || nextSeq("PINV");
  const invId = nid();
  const je = postJournal(entityId, "Provisional Invoice Raised",
    `Provisional invoice ${invNo}${sc ? ` — ${sc.contract_ref}` : ""}`,
    [{ account_code: "1200", account_name: "Accounts Receivable", debit: amount, credit: 0 },
     { account_code: "4000", account_name: "Sales Revenue", debit: 0, credit: amount }],
    "invoice", invId, vesselId, currency, 1.0, null, "Posted", "Auto");
  const inv = {
    id: invId, invoice_number: invNo, type: "Provisional", status: "Open",
    sale_contract_id: payload.sale_contract_id, sale_contract_ref: sc ? sc.contract_ref : null,
    counterparty_name: sc ? sc.counterparty_name : payload.counterparty_name,
    commodity: sc ? sc.commodity : payload.commodity,
    vessel_id: vesselId, entity_id: entityId, amount: r2(amount), currency,
    provisional_je_id: je.id, final_invoice_id: null, invoice_date: nowIso(), created_at: nowIso(), updated_at: nowIso(),
  };
  db.invoices.push(inv);
  audit("invoices", invId, "provisional", payload._actor || "system");
  return inv;
}

function raiseFinalInvoice(payload) {
  const prov = db.invoices.find((x) => x.id === payload.provisional_invoice_id);
  if (!prov) notFound("Provisional invoice");
  if (prov.status === "Finalised") err(400, "This provisional invoice is already finalised");
  const finalAmount = Number(payload.amount) || prov.amount;
  const provAmt = prov.amount;
  const entityId = prov.entity_id;
  const currency = prov.currency;
  const invNo = payload.invoice_number || nextSeq("FINV");
  const invId = nid();
  postJournal(entityId, "Provisional Invoice Reversed",
    `Reverse provisional ${prov.invoice_number}`,
    [{ account_code: "4000", account_name: "Sales Revenue", debit: provAmt, credit: 0 },
     { account_code: "1200", account_name: "Accounts Receivable", debit: 0, credit: provAmt }],
    "invoice", invId, prov.vessel_id, currency, 1.0, null, "Posted", "Auto");
  const jeFinal = postJournal(entityId, "Final Invoice Settled",
    `Final invoice ${invNo}${prov.sale_contract_ref ? ` — ${prov.sale_contract_ref}` : ""}`,
    [{ account_code: "1200", account_name: "Accounts Receivable", debit: finalAmount, credit: 0 },
     { account_code: "4000", account_name: "Sales Revenue", debit: 0, credit: finalAmount }],
    "invoice", invId, prov.vessel_id, currency, 1.0, null, "Posted", "Auto");
  const inv = {
    id: invId, invoice_number: invNo, type: "Final", status: "Settled",
    sale_contract_id: prov.sale_contract_id, sale_contract_ref: prov.sale_contract_ref,
    counterparty_name: prov.counterparty_name, commodity: prov.commodity,
    vessel_id: prov.vessel_id, entity_id: entityId, amount: r2(finalAmount), currency,
    provisional_invoice_id: prov.id, final_je_id: jeFinal.id, invoice_date: nowIso(), created_at: nowIso(), updated_at: nowIso(),
  };
  db.invoices.push(inv);
  Object.assign(prov, { status: "Finalised", final_invoice_id: invId, updated_at: nowIso() });
  audit("invoices", invId, "final", payload._actor || "system");
  return inv;
}

function glPostEvent(payload) {
  const rule = db.posting_rules.find((r) => r.event === payload.event);
  if (!rule) err(400, `No posting rule for event '${payload.event}'`);
  return postJournal(
    payload.entity_id, payload.event, payload.description || rule.description,
    [{ account_code: rule.debit_account_code, account_name: rule.debit_account_name, debit: Number(payload.amount) || 0, credit: 0 },
     { account_code: rule.credit_account_code, account_name: rule.credit_account_name, debit: 0, credit: Number(payload.amount) || 0 }],
    payload.source_type, payload.source_id, payload.vessel_id, payload.currency || "USD", Number(payload.fx_rate || 1.0),
  );
}

function confirmContract(kind, cid) {
  const c = getItem(kind === "purchase" ? "purchase-contracts" : "sale-contracts", cid);
  c.status = "Confirmed"; c.updated_at = nowIso();
  const value = Number(c.quantity || 0) * Number(c.price || 0);
  if (kind === "purchase") {
    postJournal(c.entity_id, "Purchase Contract Confirmed",
      `Purchase ${c.contract_ref} - ${c.commodity}`,
      [{ account_code: "1300", account_name: "Inventory In-Transit", debit: value, credit: 0 },
       { account_code: "2100", account_name: "Accounts Payable", debit: 0, credit: value }],
      "purchase_contract", cid, undefined, c.currency || "USD");
    audit("purchase_contracts", cid, "confirm", "system");
  } else {
    postJournal(c.entity_id, "Sale Contract Confirmed",
      `Sale ${c.contract_ref} - ${c.commodity}`,
      [{ account_code: "1200", account_name: "Accounts Receivable", debit: value, credit: 0 },
       { account_code: "4000", account_name: "Sales Revenue", debit: 0, credit: value }],
      "sale_contract", cid, undefined, c.currency || "USD");
    audit("sale_contracts", cid, "confirm", "system");
  }
  return { ok: true, value };
}

// ---------------- Computed endpoints ----------------
function computeVesselPnl(vesselId) {
  const vessel = db.vessels.find((v) => v.id === vesselId);
  if (!vessel) return null;
  const pcs = db.purchase_contracts.filter((c) => (c.vessel_ids || []).includes(vesselId));
  const scs = db.sale_contracts.filter((c) => (c.vessel_ids || []).includes(vesselId));
  const fixture = db.freight_fixtures.find((f) => f.vessel_id === vesselId);
  const hedges = db.hedge_positions.filter((h) => h.vessel_id === vesselId);
  const port_calls = db.port_calls.filter((p) => p.vessel_id === vesselId);

  const purchaseQty = pcs.reduce((s, c) => s + Number(c.quantity || 0), 0);
  const purchaseCost = pcs.reduce((s, c) => s + Number(c.quantity || 0) * Number(c.price || 0), 0);
  const saleQty = scs.reduce((s, c) => s + Number(c.quantity || 0), 0);
  const saleValue = scs.reduce((s, c) => s + Number(c.quantity || 0) * Number(c.price || 0), 0);

  const freightCost = fixture ? Number(fixture.freight_rate || 0) * (saleQty || purchaseQty) : 0;
  const demurrage = port_calls.reduce((s, pc) => s + Number(pc.demurrage_amount || 0) - Number(pc.despatch_amount || 0), 0);

  const otherCosts = Number(vessel.insurance_cost || 0) + Number(vessel.port_charges || 0) +
    Number(vessel.survey_fees || 0) + Number(vessel.brokerage || 0);
  const financingCost = Number(vessel.financing_cost || 0);

  const hedgeRealised = hedges.reduce((s, h) => s + Number(h.realised_pnl || 0), 0);
  const hedgeUnrealised = hedges
    .filter((h) => h.status === "Open")
    .reduce((s, h) => s + (Number(h.mtm_price || 0) - Number(h.entry_price || 0)) * Number(h.lots || 0) *
      Number(h.lot_size || 1) * (h.side === "Long" ? 1 : -1), 0);

  const physicalPnl = saleValue - purchaseCost - freightCost - demurrage - otherCosts - financingCost;
  const hedgePnl = hedgeRealised + hedgeUnrealised;
  const coverage = purchaseQty ? Math.round((Math.min(saleQty, purchaseQty) / purchaseQty) * 1000) / 10 : 0;

  return {
    vessel_id: vesselId, vessel_name: vessel.name, status: vessel.status,
    purchase_qty: purchaseQty, purchase_cost: r2(purchaseCost),
    avg_purchase_price: purchaseQty ? r2(purchaseCost / purchaseQty) : 0,
    sale_qty: saleQty, sale_value: r2(saleValue),
    avg_sale_price: saleQty ? r2(saleValue / saleQty) : 0,
    freight_cost: r2(freightCost), demurrage: r2(demurrage),
    other_costs: r2(otherCosts), financing_cost: financingCost,
    physical_pnl: r2(physicalPnl), hedge_realised: r2(hedgeRealised),
    hedge_unrealised: r2(hedgeUnrealised), hedge_pnl: r2(hedgePnl),
    net_pnl: r2(physicalPnl + hedgePnl), coverage_pct: coverage,
    purchase_contracts: pcs, sale_contracts: scs, fixture: fixture || null,
    hedges, port_calls,
  };
}

function vesselsPipeline() {
  return db.vessels.map((v) => {
    const pnl = computeVesselPnl(v.id);
    return { ...v, net_pnl: pnl ? pnl.net_pnl : 0, coverage_pct: pnl ? pnl.coverage_pct : 0 };
  });
}

function physicalPositions() {
  const book = {};
  for (const c of db.purchase_contracts) {
    if (c.status === "Cancelled") continue;
    const key = `${c.commodity}|${c.delivery_month || "TBD"}`;
    if (!book[key]) book[key] = { commodity: c.commodity, delivery_month: c.delivery_month || "TBD", bought: 0, sold: 0, origin: c.origin };
    book[key].bought += Number(c.quantity || 0);
  }
  for (const c of db.sale_contracts) {
    if (c.status === "Cancelled") continue;
    const key = `${c.commodity}|${c.delivery_month || "TBD"}`;
    if (!book[key]) book[key] = { commodity: c.commodity, delivery_month: c.delivery_month || "TBD", bought: 0, sold: 0, origin: "-" };
    book[key].sold += Number(c.quantity || 0);
  }
  const rows = [];
  for (const v of Object.values(book)) {
    v.net_open = r2(v.bought - v.sold);
    v.status = v.net_open > 0 ? "Long" : (v.net_open < 0 ? "Short" : "Covered");
    rows.push(v);
  }
  return rows;
}

function netExposure() {
  const physical = physicalPositions();
  const openHedges = db.hedge_positions.filter((h) => h.status === "Open");
  const exp = {};
  for (const p of physical) {
    if (!exp[p.commodity]) exp[p.commodity] = { commodity: p.commodity, physical_net: 0, hedge_net: 0 };
    exp[p.commodity].physical_net += p.net_open;
  }
  for (const h of openHedges) {
    const c = h.commodity;
    if (!exp[c]) exp[c] = { commodity: c, physical_net: 0, hedge_net: 0 };
    const lotsMt = Number(h.lots || 0) * Number(h.lot_size || 1);
    exp[c].hedge_net += h.side === "Long" ? lotsMt : -lotsMt;
  }
  const limits = { CPO: 30000, "Crude Soybean Oil": 25000, "RBD Palm Olein": 20000, Soybeans: 40000, "Sunflower Oil": 15000 };
  const rows = [];
  for (const v of Object.values(exp)) {
    v.net_exposure = r2(v.physical_net + v.hedge_net);
    v.risk_limit = limits[v.commodity] || 25000;
    v.breach = Math.abs(v.net_exposure) > v.risk_limit;
    rows.push(v);
  }
  return rows;
}

function trialBalance(entityId) {
  let entries = db.journal_entries.filter((je) => je.status === "Posted");
  if (entityId) entries = entries.filter((je) => je.entity_id === entityId);
  const acctMap = Object.fromEntries(db.accounts.map((a) => [a.code, a]));
  const balances = {};
  for (const je of entries) {
    for (const line of (je.lines || [])) {
      const code = line.account_code;
      if (!balances[code]) balances[code] = {
        account_code: code, account_name: line.account_name,
        type: (acctMap[code] || {}).type || "Asset", debit: 0, credit: 0,
      };
      balances[code].debit += Number(line.debit || 0);
      balances[code].credit += Number(line.credit || 0);
    }
  }
  const rows = [];
  for (const b of Object.values(balances)) {
    b.balance = r2(b.debit - b.credit);
    rows.push(b);
  }
  rows.sort((a, b) => a.account_code.localeCompare(b.account_code, undefined, { numeric: true }));
  return {
    rows,
    total_debit: r2(rows.reduce((s, r) => s + r.debit, 0)),
    total_credit: r2(rows.reduce((s, r) => s + r.credit, 0)),
  };
}

const pnlStatement = (entityId) => {
  const tb = trialBalance(entityId);
  const revenue = tb.rows.filter((r) => r.type === "Revenue");
  const expense = tb.rows.filter((r) => r.type === "Expense");
  const totalRevenue = r2(revenue.reduce((s, r) => s - r.balance, 0));
  const totalExpense = r2(expense.reduce((s, r) => s + r.balance, 0));
  return { revenue, expense, total_revenue: totalRevenue, total_expense: totalExpense, net_profit: r2(totalRevenue - totalExpense) };
};

const balanceSheet = (entityId) => {
  const tb = trialBalance(entityId);
  const assets = tb.rows.filter((r) => r.type === "Asset");
  const liabilities = tb.rows.filter((r) => r.type === "Liability");
  const equity = tb.rows.filter((r) => r.type === "Equity");
  return {
    assets, liabilities, equity,
    total_assets: r2(assets.reduce((s, r) => s + r.balance, 0)),
    total_liabilities: r2(liabilities.reduce((s, r) => s - r.balance, 0)),
    total_equity: r2(equity.reduce((s, r) => s - r.balance, 0)),
  };
};

function dashboardExecutive() {
  const activePcs = db.purchase_contracts.filter((c) => c.status !== "Cancelled");
  const activeScs = db.sale_contracts.filter((c) => c.status !== "Cancelled");
  const totalVolume = activeScs.reduce((s, c) => s + Number(c.quantity || 0), 0);
  const saleValue = activeScs.reduce((s, c) => s + Number(c.quantity || 0) * Number(c.price || 0), 0);
  const purchaseValue = activePcs.reduce((s, c) => s + Number(c.quantity || 0) * Number(c.price || 0), 0);

  let totalPnl = 0, realised = 0, unrealised = 0;
  for (const v of db.vessels) {
    const pnl = computeVesselPnl(v.id);
    if (pnl) { totalPnl += pnl.net_pnl; realised += pnl.hedge_realised; unrealised += pnl.hedge_unrealised; }
  }

  const lcs = db.letters_of_credit;
  const tasks = db.tasks;
  const exposures = netExposure();
  const byCommodity = {};
  for (const c of activeScs) {
    byCommodity[c.commodity] = (byCommodity[c.commodity] || 0) + Number(c.quantity || 0) * Number(c.price || 0);
  }

  return {
    total_trades: activeScs.length, total_volume: r2(totalVolume),
    total_sale_value: r2(saleValue), total_purchase_value: r2(purchaseValue),
    total_margin: r2(saleValue - purchaseValue), total_pnl: r2(totalPnl),
    realised_pnl: r2(realised), unrealised_pnl: r2(unrealised),
    active_vessels: db.vessels.filter((v) => v.status !== "Closed" && v.status !== "Settled").length,
    open_lcs: lcs.filter((l) => l.status !== "Closed").length,
    overdue_tasks: tasks.filter((t) => t.status !== "Done" && t.due_date < nowIso()).length,
    risk_breaches: exposures.filter((e) => e.breach).length,
    by_commodity: Object.entries(byCommodity).map(([commodity, value]) => ({ commodity, value: r2(value) })),
  };
}

function dealCalculation() {
  const pcMap = Object.fromEntries(db.purchase_contracts.map((c) => [c.id, c]));
  const cpMap = Object.fromEntries(db.counterparties.map((c) => [c.id, c]));
  const lcMap = {};
  for (const lc of db.letters_of_credit) if (lc.sale_contract_id) lcMap[lc.sale_contract_id] = lc;
  const rows = [];
  for (const sc of db.sale_contracts) {
    const link = db.contract_links.find((l) => l.sale_contract_id === sc.id);
    const pc = link ? pcMap[link.purchase_contract_id] : null;
    const buyer = cpMap[sc.counterparty_id] || {};
    const lc = lcMap[sc.id] || {};
    const saleVal = Number(sc.quantity || 0) * Number(sc.price || 0);
    const purVal = pc ? Number(pc.quantity || 0) * Number(pc.price || 0) : 0;
    rows.push({
      trade_no: sc.trade_no || sc.contract_ref, trader: buyer.name || "-",
      commodity: sc.commodity, quantity: sc.quantity,
      sales_contract_number: sc.contract_ref,
      purchase_contract_number: pc ? pc.contract_ref : "-",
      total_sales_contract_value: r2(saleVal),
      total_purchase_contract_value: r2(purVal),
      sale_price: sc.price, purchase_price: pc ? pc.price : null,
      lc_number: lc.lc_number || "-", status: sc.status || "Draft",
      sale_contract_id: sc.id, purchase_contract_id: pc ? pc.id : null,
    });
  }
  return rows;
}

function counterpartyExposure(cid) {
  const cp = db.counterparties.find((c) => c.id === cid);
  if (!cp) notFound("Counterparty");
  const pcs = db.purchase_contracts.filter((c) => c.counterparty_id === cid && c.status !== "Cancelled");
  const scs = db.sale_contracts.filter((c) => c.counterparty_id === cid && c.status !== "Cancelled");
  const payable = pcs.reduce((s, c) => s + Number(c.quantity || 0) * Number(c.price || 0), 0);
  const receivable = scs.reduce((s, c) => s + Number(c.quantity || 0) * Number(c.price || 0), 0);
  const used = payable + receivable;
  const limit = Number(cp.credit_limit || 0);
  return {
    counterparty: cp, payable: r2(payable), receivable: r2(receivable),
    total_exposure: r2(used), credit_limit: limit,
    utilisation_pct: limit ? Math.round((used / limit) * 1000) / 10 : 0, available: r2(limit - used),
  };
}

function allCounterpartyExposure() {
  return db.counterparties.map((cp) => {
    const e = counterpartyExposure(cp.id);
    return {
      id: cp.id, name: cp.name, type: cp.type,
      credit_limit: e.credit_limit, total_exposure: e.total_exposure,
      utilisation_pct: e.utilisation_pct, available: e.available, rating: cp.rating,
      breach: e.credit_limit ? e.total_exposure > e.credit_limit : false,
    };
  });
}

function bankFacilities() {
  const openLcs = db.letters_of_credit.filter((l) => l.status !== "Closed");
  return db.banks.map((b) => {
    const drawn = openLcs.filter((l) => l.bank_id === b.id).reduce((s, l) => s + Number(l.amount || 0), 0);
    const limit = Number(b.facility_limit || 0);
    return {
      id: b.id, name: b.name, entity_id: b.entity_id,
      facility_limit: limit, drawn: r2(drawn), available: r2(limit - drawn),
      utilisation_pct: limit ? Math.round((drawn / limit) * 1000) / 10 : 0,
    };
  });
}

function notifications() {
  const alerts = [];
  const today = new Date();
  for (const lc of db.letters_of_credit.filter((l) => l.status !== "Closed")) {
    const d = Math.floor((new Date(lc.expiry_date) - today) / DAY);
    if (d >= 0 && d <= 21) alerts.push({ type: "lc_expiry", severity: "warning", message: `LC ${lc.lc_number} expires in ${d} days` });
  }
  for (const v of db.vessels) {
    const d = Math.floor((new Date(v.laycan_start) - today) / DAY);
    if (d >= 0 && d <= 10 && (v.status === "Nominated" || v.status === "Laycan Agreed")) {
      alerts.push({ type: "laycan", severity: "info", message: `Vessel ${v.name} laycan starts in ${d} days` });
    }
  }
  for (const e of netExposure()) {
    if (e.breach) alerts.push({ type: "risk", severity: "critical", message: `${e.commodity} net position ${e.net_exposure.toLocaleString("en-US", { maximumFractionDigits: 0 })} MT exceeds limit` });
  }
  for (const e of allCounterpartyExposure()) {
    if (e.breach) alerts.push({ type: "credit", severity: "critical", message: `${e.name} exceeds credit limit (${Math.round(e.utilisation_pct)}% used)` });
  }
  return alerts;
}

// ---------------- Fund flow ----------------
const MONTHS = { jan: 1, feb: 2, mar: 3, apr: 4, may: 5, jun: 6, jul: 7, aug: 8, sep: 9, oct: 10, nov: 11, dec: 12 };

function monthToDate(m, day = 15) {
  try {
    const parts = String(m).toLowerCase().split("-");
    const mon = MONTHS[parts[0].slice(0, 3)];
    const yr = 2000 + Number(parts[1]);
    return new Date(Date.UTC(yr, mon - 1, day));
  } catch {
    return null;
  }
}

const shift = (dt, d) => new Date(new Date(dt).getTime() + d * DAY).toISOString();

function computeActual(entityId, start, end) {
  let allJe = db.journal_entries.filter((je) => je.status === "Posted");
  if (entityId && entityId !== "all") allJe = allJe.filter((je) => je.entity_id === entityId);
  const jes = allJe.filter((je) => start.slice(0, 10) <= (je.entry_date || "").slice(0, 10) && (je.entry_date || "").slice(0, 10) <= end.slice(0, 10));
  const acct = Object.fromEntries(db.accounts.map((a) => [a.code, a]));
  const delta = {};
  const jeIds = {};
  for (const je of jes) {
    for (const l of (je.lines || [])) {
      const code = l.account_code;
      delta[code] = (delta[code] || 0) + Number(l.debit || 0) - Number(l.credit || 0);
      if (!jeIds[code]) jeIds[code] = new Set();
      jeIds[code].add(je.id);
    }
  }
  const revenue = Object.entries(acct).reduce((s, [c, a]) => (a.type === "Revenue" ? s - (delta[c] || 0) : s), 0);
  const expense = Object.entries(acct).reduce((s, [c, a]) => (a.type === "Expense" ? s + (delta[c] || 0) : s), 0);
  const netProfit = r2(revenue - expense);
  const revExpIds = [...new Set(
    Object.entries(acct)
      .filter(([, a]) => a.type === "Revenue" || a.type === "Expense")
      .flatMap(([c]) => [...(jeIds[c] || [])]),
  )];

  const sources = [], applications = [];
  const op = {
    label: "Funds from operations (net profit for period)", amount: r2(Math.abs(netProfit)),
    codes: Object.entries(acct).filter(([, a]) => a.type === "Revenue" || a.type === "Expense").map(([c]) => c),
    je_ids: revExpIds,
  };
  (netProfit >= 0 ? sources : applications).push(op);

  for (const [code, a] of Object.entries(acct)) {
    if (code === "1000" || a.type === "Revenue" || a.type === "Expense") continue;
    const d = r2(delta[code] || 0);
    if (Math.abs(d) < 0.01) continue;
    const name = a.name;
    const ids = [...(jeIds[code] || [])];
    if (a.type === "Asset") {
      if (d > 0) applications.push({ label: `Increase in ${name}`, amount: d, codes: [code], je_ids: ids });
      else sources.push({ label: `Decrease in ${name}`, amount: -d, codes: [code], je_ids: ids });
    } else {
      if (d < 0) sources.push({ label: `Increase in ${name}`, amount: -d, codes: [code], je_ids: ids });
      else applications.push({ label: `Decrease in ${name}`, amount: d, codes: [code], je_ids: ids });
    }
  }

  const totalSources = r2(sources.reduce((s, x) => s + x.amount, 0));
  const totalApplications = r2(applications.reduce((s, x) => s + x.amount, 0));
  return {
    start, end, net_profit: netProfit,
    sources, applications,
    total_sources: totalSources, total_applications: totalApplications,
    net_change: r2(totalSources - totalApplications),
    cash_movement: r2(delta["1000"] || 0),
    entry_count: jes.length,
  };
}

function fundFlowActual(params = {}) {
  const now = new Date();
  const start = params.start || new Date(now.getFullYear(), now.getMonth(), 1).toISOString();
  const end = params.end || now.toISOString();
  const current = computeActual(params.entity_id, start, end);
  let prior = null;
  if (params.prev_start && params.prev_end) prior = computeActual(params.entity_id, params.prev_start, params.prev_end);
  return { current, prior };
}

function fundFlowForecast(entityId) {
  const ent = entityId && entityId !== "all" ? entityId : null;
  const matches = (d) => !ent || d.entity_id === ent;
  const pcs = db.purchase_contracts.filter(matches);
  const scs = db.sale_contracts.filter(matches);
  const vessels = Object.fromEntries(db.vessels.map((v) => [v.id, v]));
  const fixtures = db.freight_fixtures;
  const portCalls = db.port_calls;
  const lcs = db.letters_of_credit.filter(matches);
  const hedges = db.hedge_positions.filter(matches);

  const now = new Date();
  const events = [];

  const payOffset = (term) => {
    const t = (term || "").toLowerCase();
    if (t.includes("usance") || t === "da") return 90;
    if (t.includes("sight")) return 5;
    return 30;
  };

  for (const c of pcs) {
    if (c.status === "Cancelled" || c.status === "Draft") continue;
    const d = monthToDate(c.delivery_month);
    if (!d) continue;
    const val = Number(c.quantity || 0) * Number(c.price || 0);
    const conf = c.status === "Confirmed" && c.pricing_basis === "Fixed" ? "Confirmed" : "Probable";
    events.push({
      date: shift(d, payOffset(c.payment_term) - 15), amount: -r2(val), direction: "out", confidence: conf,
      category: "Purchase Payment", label: `${c.contract_ref} · ${c.commodity}`, ref: c.contract_ref,
      source_type: "purchase_contract", source_id: c.id, currency: c.currency || "USD",
    });
  }

  for (const c of scs) {
    if (c.status === "Cancelled" || c.status === "Draft") continue;
    const d = monthToDate(c.delivery_month);
    if (!d) continue;
    const val = Number(c.quantity || 0) * Number(c.price || 0);
    const conf = c.status === "Confirmed" && c.pricing_basis === "Fixed" ? "Confirmed" : "Probable";
    events.push({
      date: shift(d, payOffset(c.payment_term) + 5), amount: r2(val), direction: "in", confidence: conf,
      category: "Sale Collection", label: `${c.contract_ref} · ${c.commodity}`, ref: c.contract_ref,
      source_type: "sale_contract", source_id: c.id, currency: c.currency || "USD",
    });
  }

  for (const fx of fixtures) {
    const v = vessels[fx.vessel_id];
    if (!v || v.status === "Settled" || v.status === "Closed") continue;
    const vscs = scs.filter((c) => (c.vessel_ids || []).includes(fx.vessel_id));
    const qty = vscs.reduce((s, c) => s + Number(c.quantity || 0), 0) || v.dwt || 0;
    const freight = Number(fx.freight_rate || 0) * qty;
    if (freight && v.laycan_start) {
      events.push({
        date: shift(new Date(v.laycan_start), 7), amount: -r2(freight), direction: "out", confidence: "Confirmed",
        category: "Freight Payment", label: `${v.name} freight ${fx.load_port}→${fx.discharge_port}`,
        ref: v.name, source_type: "vessel", source_id: v.id, currency: "USD",
      });
    }
  }

  for (const pc of portCalls) {
    const netDem = Number(pc.demurrage_amount || 0) - Number(pc.despatch_amount || 0);
    const v = vessels[pc.vessel_id];
    if (netDem > 0 && v && v.laycan_start && v.status !== "Settled" && v.status !== "Closed") {
      events.push({
        date: shift(new Date(v.laycan_start), 25), amount: -r2(netDem), direction: "out", confidence: "Probable",
        category: "Demurrage", label: `${v.name} demurrage @ ${pc.port}`, ref: v.name,
        source_type: "vessel", source_id: v.id, currency: "USD",
      });
    }
  }

  for (const lc of lcs) {
    if (lc.status === "Closed") continue;
    const margin = r2(Number(lc.amount || 0) * 0.10);
    events.push({
      date: shift(now, 1), amount: -margin, direction: "out", confidence: "Confirmed",
      category: "LC Margin Block", label: `LC ${lc.lc_number} margin (10%)`,
      ref: lc.lc_number, source_type: "letter_of_credit", source_id: lc.id, currency: lc.currency || "USD",
    });
  }

  for (const h of hedges) {
    if (h.status !== "Open") continue;
    const unreal = (Number(h.mtm_price || 0) - Number(h.entry_price || 0)) * Number(h.lots || 0) *
      Number(h.lot_size || 1) * (h.side === "Long" ? 1 : -1);
    const notional = Number(h.entry_price || 0) * Number(h.lots || 0) * Number(h.lot_size || 1);
    events.push({
      date: shift(now, 3), amount: r2(Math.min(0, unreal)), direction: "out", confidence: "Probable",
      category: "Hedge Variation Margin",
      label: `${h.commodity} ${h.exchange} ${h.contract_month} (${h.side})`,
      ref: `${h.exchange}-${h.contract_month}`, source_type: "hedge_position", source_id: h.id,
      currency: h.currency || "USD",
      hedge_meta: { notional: r2(notional), side: h.side, lots: h.lots, lot_size: h.lot_size, entry_price: h.entry_price, mtm_price: h.mtm_price },
    });
  }

  let openingCash = 0;
  for (const je of db.journal_entries.filter((x) => x.status === "Posted")) {
    if (ent && je.entity_id !== ent) continue;
    for (const l of (je.lines || [])) {
      if (l.account_code === "1000") openingCash += Number(l.debit || 0) - Number(l.credit || 0);
    }
  }
  const openLcs = lcs.filter((l) => l.status !== "Closed");
  const facilityLimit = db.banks
    .filter((b) => !ent || b.entity_id === ent)
    .reduce((s, b) => s + Number(b.facility_limit || 0), 0);
  const facilityDrawn = openLcs.reduce((s, l) => s + Number(l.amount || 0), 0);

  events.sort((a, b) => (a.date || "").localeCompare(b.date || ""));
  return {
    opening_cash: r2(openingCash),
    facility_limit: r2(facilityLimit),
    facility_drawn: r2(facilityDrawn),
    facility_available: r2(facilityLimit - facilityDrawn),
    as_of: now.toISOString(),
    events,
  };
}

// ---------------- Router ----------------
const seg = (path) => String(path || "").replace(/^\/+|\/+$/g, "").split("/").filter(Boolean);

export async function handleRequest(method, path, params = {}, body = {}) {
  const parts = seg(path);
  const [a, b, c] = parts;

  if (method === "GET") {
    if (path === "/" || parts.length === 0) return { message: "Riya Agro Singapore ERP API" };

    if (a === "audit-logs") {
      const query = params.record_id ? db.audit_logs.filter((l) => l.record_id === params.record_id) : db.audit_logs;
      const sorted = [...query].sort((x, y) => (y.timestamp || "").localeCompare(x.timestamp || ""));
      return sorted.slice(0, Number(params.limit || 200));
    }
    if (a === "vessels-pipeline") return vesselsPipeline();
    if (a === "positions") {
      if (b === "physical") return physicalPositions();
      if (b === "net-exposure") return netExposure();
    }
    if (a === "accounting") {
      if (b === "trial-balance") return trialBalance(params.entity_id);
      if (b === "pnl-statement") return pnlStatement(params.entity_id);
      if (b === "balance-sheet") return balanceSheet(params.entity_id);
    }
    if (a === "dashboard") {
      if (b === "executive") return dashboardExecutive();
      if (b === "deal-calculation") return dealCalculation();
    }
    if (a === "counterparties-exposure") return allCounterpartyExposure();
    if (a === "bank-facilities") return bankFacilities();
    if (a === "notifications") return notifications();
    if (a === "fund-flow") {
      if (b === "actual") return fundFlowActual(params);
      if (b === "forecast") return fundFlowForecast(params.entity_id);
    }
    if (a === "vessels" && b && c === "pnl") {
      const pnl = computeVesselPnl(b);
      if (!pnl) notFound("Vessel");
      return pnl;
    }
    if (a === "counterparties" && b && c === "exposure") return counterpartyExposure(b);
    if (b) return getItem(a, b);
    return listItems(a);
  }

  if (method === "POST") {
    if (a === "journal-entries" && b === "manual") return createManualJe(body);
    if (a === "journal-entries" && b && (c === "approve" || c === "reject")) {
      return c === "approve" ? approveJe(b, body) : rejectJe(b, body);
    }
    if (a === "periods" && b && (c === "lock" || c === "unlock")) return togglePeriod(b, c === "lock");
    if (a === "invoices" && b === "provisional") return raiseProvisionalInvoice(body);
    if (a === "invoices" && b === "final") return raiseFinalInvoice(body);
    if (a === "gl" && b === "post-event") return glPostEvent(body);
    if (a === "purchase-contracts" && b && c === "confirm") return confirmContract("purchase", b);
    if (a === "sale-contracts" && b && c === "confirm") return confirmContract("sale", b);
    if (a === "seed") return reseed();
    return createItem(a, body);
  }

  if (method === "PUT") {
    return updateItem(a, b, body);
  }

  if (method === "DELETE") {
    return deleteItem(a, b);
  }

  return { ok: true };
}